import React from "react";
import {Button, Card, CardBody, CardHeader, Input} from "gd-react";
import gatewayCommandSocket, {LOG_STREAM_METHOD} from "../../services/gatewayCommandSocket";
import {LOG_STREAM_DEFAULT_UNIT} from "@gridduck/gateway-rpc";
import GatewayStatsReport from "./GatewayStatsReport";

// journalctl -f on a busy unit emits faster than React can usefully re-render, so chunks are
// buffered and flushed on a tick rather than each one setting state.
const FLUSH_INTERVAL_MS = 200;

// Enough to scroll back through a problem, bounded so a tail left running overnight cannot
// grow the tab's memory without limit.
const MAX_LINES = 2000;

// How close to the bottom still counts as "following". Anything further up and the reader has
// deliberately scrolled back, so new lines must not yank them away from what they are reading.
const FOLLOW_THRESHOLD_PX = 40;

const DEFAULT_UNIT = LOG_STREAM_DEFAULT_UNIT;

// Not persisted on the device: the next config from the cloud puts debug_mode back.
const SET_DEBUG_METHOD = 'gateway.setDebug';
const INFO_METHOD = 'gateway.info';
// The hub writes the report to its journal as well as answering, so a running tail shows it.
const STATS_METHOD = 'gateway.stats';

class GatewayLogs extends React.Component {

	constructor(props) {
		super(props);
		this.state = {
			unit: DEFAULT_UNIT,
			lines: [],
			streaming: false,
			status: null,
			error: null,
			following: true,
			// null until the hub has been asked. The tab is shown for any Pi-Duck, but firmware
			// that predates the command channel announces nothing, and saying so up front beats
			// letting Start fail with UNKNOWN_METHOD.
			supported: null,
			// null while unknown: the hub has not said, or firmware without gateway.info.
			debug: null,
			debugSupported: false,
			statsSupported: false,
			// The last gateway.stats result, shown as tables above the log
			report: null,
		};

		// Not state: these change per chunk and nothing renders off them directly.
		this.handle = null;
		this.buffer = [];
		this.flushTimer = null;
		this.paneRef = React.createRef();

		this.start = this.start.bind(this);
		this.stop = this.stop.bind(this);
		this.clear = this.clear.bind(this);
		this.toggleDebug = this.toggleDebug.bind(this);
		this.printStats = this.printStats.bind(this);
		this.onScroll = this.onScroll.bind(this);
	}

	componentDidMount() {
		const self = this;
		const gatewayId = this.props.item.id;
		gatewayCommandSocket.capabilities(gatewayId)
			.then(function (res) {
				if (self.unmounted) return;
				const methods = res.methods || [];
				self.setState({
					supported: methods.indexOf(LOG_STREAM_METHOD) !== -1,
					debugSupported: methods.indexOf(SET_DEBUG_METHOD) !== -1,
					statsSupported: methods.indexOf(STATS_METHOD) !== -1,
				});
				if (methods.indexOf(INFO_METHOD) !== -1) return self.readDebug();
			})
			.catch(function () {
				// Unreachable is not the same as unsupported, so this does not claim the hub
				// cannot stream - only that we could not ask.
				if (self.unmounted) return;
				self.setState({supported: false, error: 'Could not reach the hub to check what it supports.'});
			});
	}

	readDebug() {
		const self = this;
		return gatewayCommandSocket.invoke(this.props.item.id, INFO_METHOD)
			.then(function (info) {
				if (self.unmounted || !info || typeof info.debug !== 'boolean') return;
				self.setState({debug: info.debug});
			})
			// Only the label depends on it, so an unanswered info leaves the toggle usable as "Start debug".
			.catch(function () {});
	}

	componentWillUnmount() {
		this.unmounted = true;
		// Cancels on the device too, so leaving the tab does not leave journalctl running on
		// the hub for the rest of the deadline.
		this.teardown();
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.lines !== this.state.lines && this.state.following) {
			const pane = this.paneRef.current;
			if (pane) pane.scrollTop = pane.scrollHeight;
		}
	}

	teardown() {
		clearInterval(this.flushTimer);
		this.flushTimer = null;
		this.buffer = [];
		if (this.handle) {
			this.handle.stop();
			this.handle = null;
		}
	}

	flush() {
		if (!this.buffer.length) return;
		const incoming = this.buffer;
		this.buffer = [];
		this.setState(function (prev) {
			const next = prev.lines.concat(incoming);
			return {lines: next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next};
		});
	}

	onScroll() {
		const pane = this.paneRef.current;
		if (!pane) return;
		const atBottom = pane.scrollHeight - pane.scrollTop - pane.clientHeight <= FOLLOW_THRESHOLD_PX;
		if (atBottom !== this.state.following) this.setState({following: atBottom});
	}

	async start() {
		const self = this;
		if (this.handle) return;

		this.setState({error: null, status: 'Connecting…', streaming: true, following: true});

		try {
			this.handle = await gatewayCommandSocket.stream(
				this.props.item.id,
				LOG_STREAM_METHOD,
				// No backlog: this follows the unit from now on rather than replaying history.
				{unit: this.state.unit || DEFAULT_UNIT},
				{
					// Firmware and dashboard ship separately, so every shape is accepted: a current
					// hub batches lines per read, older ones sent one at a time.
					onChunk: function (chunk) {
						if (!chunk) return;

						// Proves the chain is live before the unit has said anything, which is
						// otherwise indistinguishable from nothing getting through.
						if (chunk.status === 'following') {
							self.setState({status: 'Following ' + chunk.unit + ' — nothing logged yet'});
							return;
						}

						if (typeof chunk.stderr === 'string') {
							self.buffer.push('[journalctl] ' + chunk.stderr);
							return;
						}

						if (Array.isArray(chunk.lines)) {
							for (let i = 0; i < chunk.lines.length; i++) {
								if (typeof chunk.lines[i] === 'string') self.buffer.push(chunk.lines[i]);
							}
						} else if (typeof chunk.line === 'string') {
							self.buffer.push(chunk.line);
						}
					},
					// The device ends the stream itself when journalctl exits or its ceiling
					// passes; the button has to come back without the reader pressing Stop.
					onEnd: function (result) {
						self.teardown();
						self.setState({
							streaming: false,
							status: 'Stream ended' + (result && result.lines ? ' after ' + result.lines + ' lines' : ''),
						});
					},
					onError: function (err) {
						self.teardown();
						self.setState({streaming: false, status: null, error: err.message});
					},
				}
			);

			this.setState({status: 'Streaming ' + (this.state.unit || DEFAULT_UNIT)});
			this.flushTimer = setInterval(function () {
				self.flush();
			}, FLUSH_INTERVAL_MS);
		} catch (err) {
			this.handle = null;
			this.setState({streaming: false, status: null, error: err.message});
		}
	}

	// Async because the Button renders with progressRes, which does onClick(evt).then(...) and
	// would throw on a plain return.
	async stop() {
		this.flush();
		this.teardown();
		this.setState({streaming: false, status: 'Stopped'});
	}

	// Async for the same progressRes reason as stop().
	async toggleDebug() {
		const enabled = this.state.debug !== true;
		this.setState({error: null});
		try {
			const res = await gatewayCommandSocket.invoke(this.props.item.id, SET_DEBUG_METHOD, {enabled});
			if (this.unmounted) return;
			const debug = res && typeof res.debug === 'boolean' ? res.debug : enabled;
			this.setState({
				debug,
				status: debug
					? 'Debug logging on until you stop it or the hub next receives its config'
					: 'Debug logging off',
			});
		} catch (err) {
			if (this.unmounted) return;
			this.setState({error: 'Could not ' + (enabled ? 'start' : 'stop') + ' debug logging: ' + err.message});
		}
	}

	// Async for the same progressRes reason as stop().
	async printStats() {
		this.setState({error: null});
		try {
			const report = await gatewayCommandSocket.invoke(this.props.item.id, STATS_METHOD);
			if (this.unmounted) return;
			this.setState({report, status: 'Stats report updated, and printed to the hub log'});
		} catch (err) {
			if (this.unmounted) return;
			this.setState({error: 'Could not print the stats report: ' + err.message});
		}
	}

	clear() {
		this.buffer = [];
		this.setState({lines: [], following: true});
	}

	render() {
		const self = this;
		const streaming = this.state.streaming;

		return (
			<div className={'details'}>
				<div className={'detail-content'}>
					{this.state.report ?
						<Card>
							<CardHeader title={'Stats report'}/>
							<CardBody>
								<GatewayStatsReport report={this.state.report}/>
							</CardBody>
						</Card> : null}
					<Card>
						<CardHeader title={'Live Logs'}/>
						<CardBody>
							<div style={{display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap'}}>
								<div style={{width: '240px'}}>
									<Input
										top='true'
										label={'Unit'}
										name={'log-unit'}
										placeholder={DEFAULT_UNIT}
										value={this.state.unit}
										disabled={streaming}
										onChange={(e) => self.setState({unit: e.target.value})}/>
								</div>
								<div style={{width: '160px'}}>
									<Button
										progressRes
										disabled={this.state.supported === false}
										color={streaming ? 'gd-red' : 'gd-blue'}
										label={streaming ? 'Stop' : 'Start'}
										onClick={streaming ? this.stop : this.start}/>
								</div>
								<div style={{width: '160px'}}>
									<Button
										progressRes
										disabled={!this.state.debugSupported}
										color={this.state.debug ? 'gd-red' : 'gd-blue'}
										label={this.state.debug ? 'Stop debug' : 'Start debug'}
										onClick={this.toggleDebug}/>
								</div>
								<div style={{width: '160px'}}>
									<Button
										progressRes
										disabled={!this.state.statsSupported}
										color={'gd-blue'}
										label={'Print stats'}
										onClick={this.printStats}/>
								</div>
								<div style={{width: '120px'}}>
									<Button
										color={'gd-grey'}
										label={'Clear'}
										onClick={this.clear}/>
								</div>
							</div>

							{this.state.supported === false ?
								<p style={{color: '#8a6d3b', fontSize: '11.5px', margin: '10px 0 0'}}>
									This hub has not announced log streaming. Its firmware predates the
									command channel, or it is currently offline.
								</p> : null}

							<p style={{fontSize: '11px', margin: '10px 0 0', minHeight: '14px'}}>
								{this.state.error
									? <span style={{color: '#c0392b'}}>{this.state.error}</span>
									: this.state.status}
								{streaming && !this.state.following
									? <span style={{color: '#8a6d3b', marginLeft: '8px'}}>
										paused scrolling — scroll to the bottom to follow again
									</span>
									: null}
							</p>

							<div
								ref={this.paneRef}
								onScroll={this.onScroll}
								style={{
									marginTop: '10px',
									height: '520px',
									overflowY: 'auto',
									overflowX: 'auto',
									background: '#11181c',
									color: '#dce5e5',
									borderRadius: '3px',
									padding: '10px 12px',
									fontFamily: 'IBM Plex Mono, Menlo, Consolas, monospace',
									fontSize: '11.5px',
									lineHeight: '1.5',
									whiteSpace: 'pre',
								}}>
								{this.state.lines.length === 0
									? <span style={{color: '#5e7480'}}>
										{streaming ? 'Waiting for output…' : 'Press Start to tail the hub\'s journal.'}
									</span>
									: this.state.lines.map(function (line, i) {
										return <div key={i}>{line}</div>;
									})}
							</div>

							{this.state.lines.length >= MAX_LINES ?
								<p style={{fontSize: '11px', color: '#5e7480', margin: '6px 0 0'}}>
									Showing the most recent {MAX_LINES} lines.
								</p> : null}
						</CardBody>
					</Card>
				</div>
			</div>
		);
	}
}

export default GatewayLogs;
